<script setup lang="ts">
import { nextTick, onMounted, ref } from 'vue'

type TEmail = {
    email: string
    isValidFormat: boolean
    isAlreadyUsed: boolean
}

const props = withDefaults(defineProps<{
    modelValue?: TEmail[]
    availableSeats?: number
    disabledEmails?: string[]
}>(), {
    modelValue: () => [],
    availableSeats: 0,
    disabledEmails: () => [],
})

const emit = defineEmits<{
    'update:modelValue': [emails: TEmail[]]
}>()

const emailTextarea = ref<HTMLElement | null>(null)
const emails = ref<TEmail[]>([])
const emailText = ref('')
const focus = ref(false)
const hover = ref(false)

// Must have at least 1 character before the @, at least 1 character after the @, a period, and at least
// 2 characters after the period
const validEmailRegex = /^[A-Z0-9a-z._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,64}$/

onMounted(() => {
    emails.value = props.modelValue
})

const isValidEmail = (email: string) => {
    // Passes generic regex validation
    return validEmailRegex.test(email)
}

const isAlreadyUsedEmail = (email: string) => {
    // Is NOT a disabled email
    return props.disabledEmails.includes(email)
        // Doesn't already exist in our email structure
        || !!emails.value.find(obj => obj.email === email)
}

const processEmails = () => {
    const newEmails = emailText.value
        .split(/\n|\t|,|;|\s|\r/)
        .map(email => {
            if (email.startsWith('<') && email.endsWith('>')) {
                // Remove the beginning and ending <>
                email = email.substring(1, email.length - 1)
            }
            return email.trim().toLowerCase()
        })
        .filter(email => !!email)

    const validatedNewEmails = newEmails.map(email => {
        return {
            email,
            isValidFormat: isValidEmail(email),
            isAlreadyUsed: isAlreadyUsedEmail(email),
        }
    })

    nextTick(() => {
        emailText.value = ''
    })
    emails.value = [
        ...emails.value,
        ...validatedNewEmails,
    ]

    emitEmails(emails.value)
    return emails.value
}

const emitEmails = (e: TEmail[]) => {
    emit('update:modelValue', e)
    return e
}

const removeEmail = (email: TEmail) => {
    emails.value.splice(emails.value.indexOf(email), 1)

    const dupedRecord = emails.value.find(e => e.email === email.email)

    if (dupedRecord && isValidEmail(email.email)) {
        // Have this record in the structure twice and it's actually valid..
        const index = emails.value.indexOf(dupedRecord)
        const record = index >= 0 ? emails.value[index] : undefined
        if (record) {
            record.isValidFormat = true
        }
    }

    emit('update:modelValue', emails.value)
    return emails.value
}

const handleDeleteInput = (e: KeyboardEvent) => {
    // delete on backspace
    if (e.key === 'Backspace' && emailText.value === '') {
        emails.value.splice(-1, 1)
    }

    const field = emailTextarea.value as HTMLElement
    field.scrollTop = field.scrollHeight

    setTimeout(() => {
        (document.querySelector('.email-textarea__input') as HTMLInputElement)?.focus()
    }, 0)

    emit('update:modelValue', emails.value)
    return emails.value
}

const handleInput = (e: KeyboardEvent) => {
    // validate on comma, enter, space, or semicolon
    if (e.key === ' ' || e.key === ',' || e.key === 'Enter' || e.key === ';') {
        processEmails()
    }

    const field = emailTextarea.value as HTMLElement
    field.scrollTop = field.scrollHeight

    setTimeout(() => {
        (document.querySelector('.email-textarea__input') as HTMLInputElement)?.focus()
    }, 0)
}

const handlePaste = async (e: ClipboardEvent) => {
    if (e.type === 'paste') {
        // pasting causes a delay in vue gaining access to new text so we delay until vue has text
        await new Promise(res => setTimeout(res, 1))
    }

    processEmails()

    const field = emailTextarea.value as HTMLElement
    field.scrollTop = field.scrollHeight

    setTimeout(() => {
        (document.querySelector('.email-textarea__input') as HTMLInputElement)?.focus()
    }, 0)
}
</script>

<template>
    <div
        ref="emailTextarea"
        class="email-textarea"
        :class="{ hover, focus }"
        @mouseover="hover = true"
        @mouseout="hover = false"
    >
        <div
            v-for="(email, index) in emails"
            :key="`${email}-${index}`"
            class="email-textarea__email"
            :class="{ 'email-textarea__email-alert': ((index + 1) - emails.length) > availableSeats }"
            :contenteditable="false"
        >
            <div
                v-if="email.isValidFormat && !email.isAlreadyUsed"
                class="email-textarea__email-pill"
            >
                {{ email.email }}
                <div class="email-textarea__email-remove" @click="removeEmail(email)">
                    &times;
                </div>
            </div>
            <div
                v-else-if="!email.isValidFormat || email.isAlreadyUsed"
                class="email-textarea__email-invalid-pill"
            >
                {{ email.email }}
                <div class="email-textarea__email-remove" @click="removeEmail(email)">
                    &times;
                </div>
            </div>
        </div>
        <textarea
            v-model="emailText"
            :contenteditable="true"
            class="email-textarea__input"
            :placeholder="emails.length || focus ? '' : 'papercutssuck@pocketprep.com, letsprep@pocketprep.com'"
            @keyup="handleInput"
            @keydown.delete="handleDeleteInput"
            @paste="handlePaste"
            @focus="focus = true"
            @blur="processEmails() && (focus = false)"
        />
    </div>
</template>

<style lang="scss" scoped>


.email-textarea {
    border: 1px solid $gray-divider;
    padding: 12px 11px;
    border-radius: 3px;
    font-size: 15px;
    line-height: 20px;
    display: flex;
    flex-direction: column;
    height: 220px;
    overflow: auto;
    box-sizing: border-box;

    &__input {
        display: block;
        height: 100%;
        outline: none;
        border: 0;
        min-height: 90px;
        padding: 0;
        resize: none;
    }

    &__email {
        margin-bottom: 9px;
        position: relative;
        display: inline-block;
        clear: both;
    }

    &__email-alert > div {
        background: $white;
        border: 1px solid $ultra-red;
    }

    &__email-invalid-pill {
        background: $white;
        border: 1px solid $ultra-red;
        font-size: 14px;
        line-height: 14px;
        padding: 4px 26px 4px 4px;
        border-radius: 4px;
        position: relative;
        color: $brand-black;
        display: inline-block;
        user-select: none;
    }

    &__email-pill {
        background: $sky-blue;
        border: 1px solid $sky-blue;
        font-size: 14px;
        line-height: 14px;
        padding: 4px 26px 4px 4px;
        border-radius: 4px;
        position: relative;
        color: $brand-black;
        display: inline-block;
        user-select: none;
    }

    &__email-remove {
        cursor: pointer;
        right: -2px;
        top: 2.5px;
        color: $brand-blue;
        position: absolute;
        font-size: 18px;
        line-height: 18px;
        width: 18px;
        height: 18px;
    }

    &.hover {
        border: 1px solid $baby-blue;
    }

    &.focus {
        border: 1px solid $brand-blue;
    }
}
</style>
